import { useState, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { DocumentForm } from '../../documents/form/DocumentForm';
import { DocumentSchema } from '../../documents/types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { PolicyMetadataBar, PolicyMetadata } from './PolicyMetadataBar';
import { LivePreviewPanel } from './LivePreviewPanel';
import { PolicyProvider } from './PolicyContext';
import { PopulatedPolicy } from '../../../services/api/policyService';
import type { VariableOption } from '../../documents/form/inputs/mentionSuggestion';
import { DocumentStatus } from '../../documents/types';
import { useSplitPane } from '../../../hooks/useSplitPane';
import { getDepartmentMembers } from '../../../services/apiService';
import AuthContext from '../../../store/auth-context';
import { createEmptyPolicyConditionalsValue } from '../conditionals/conditionalsUtils';
import { buildPolicyDocumentFunctions } from '../services/policyDocumentFunctions';
import { addMonths } from '../utils/utils';
import { useMediaQuery } from '../../../hooks/useMediaQuery';

interface PolicyEditorContentProps {
    schema: DocumentSchema;
    initialPolicyId: string | undefined;
    initialDocData: { data: Record<string, any>; status: DocumentStatus } | undefined;
    variableMap: Record<string, string>;
    variableOptions: VariableOption[];
    templateIdParam: string | null;
    schemaKeyParam: string | null;
    initialMeta: PolicyMetadata;
}

export function PolicyEditorContent({
    schema,
    initialPolicyId,
    initialDocData,
    variableMap,
    variableOptions,
    templateIdParam,
    schemaKeyParam,
    initialMeta,
}: PolicyEditorContentProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const authContext = useContext(AuthContext);

    const [policyId, setPolicyId] = useState<string | undefined>(
        initialPolicyId
    );
    const [showPreview, setShowPreview] = useState(true);
    const [mobileView, setMobileView] = useState<'form' | 'preview'>('form');
    const [activeEditorTab, setActiveEditorTab] = useState<'content' | 'conditionals'>(
        'content'
    );
    const { splitContainerRef, splitPercent, handleDragStart } = useSplitPane(50);
    const isDesktopEditor = useMediaQuery('(min-width: 1024px)');

    const [departmentUsers, setDepartmentUsers] = useState<Array<{ _id: string; name: string; role?: string }>>([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [isExpiryDateAutoFilled, setIsExpiryDateAutoFilled] = useState(false);
    const lastAutoFilledExpiryRef = useRef<string | null>(null);

    const metaForm = useForm<PolicyMetadata>({ defaultValues: initialMeta });

    useEffect(() => {
        let active = true;
        const load = async () => {
            try {
                const [users, info] = await Promise.all([
                    getDepartmentMembers(),
                    authContext.getAllInfo(),
                ]);
                if (!active) return;

                setDepartmentUsers(
                    users.map((u) => ({
                        _id: String(u.id),
                        name: u.name,
                        role: u.role,
                    }))
                );

                const currentUserId = String(info.user?._id || info.user?.id || '');
                const currentUserName = String(info.user?.name || '');
                const existingCoAuthors = metaForm.getValues('coAuthors');
                if (
                    existingCoAuthors.length === 0 &&
                    currentUserId &&
                    currentUserName
                ) {
                    metaForm.setValue('coAuthors', [
                        { user_id: currentUserId, name: currentUserName },
                    ]);
                }
            } catch {
                if (active) setDepartmentUsers([]);
            } finally {
                if (active) setUsersLoading(false);
            }
        };
        load();
        return () => {
            active = false;
        };
    }, [authContext, metaForm]);

    const handleCreated = useCallback(
        (newPolicy: PopulatedPolicy) => {
            setPolicyId(newPolicy._id);
            navigate(`/department-policies/${newPolicy._id}/edit`, {
                replace: true,
            });
        },
        [navigate]
    );

    const handleCompleted = useCallback(() => {
        navigate('/department-policies', { replace: true });
    }, [navigate]);

    const handleSavedAndClose = useCallback(() => {
        navigate('/department-policies', { replace: true });
    }, [navigate]);

    const title = metaForm.watch('title');
    const effectiveDate = metaForm.watch('effectiveDate');
    const expiryDate = metaForm.watch('expiryDate');
    const documentStatus = initialDocData?.status || 'draft';
    const policyFunctions = useMemo(() => buildPolicyDocumentFunctions(), []);
    const reviewMonths = parseInt(variableMap['policyReviewInterval'], 10);
    const hasDepartmentReviewInterval =
        !isNaN(reviewMonths) && reviewMonths > 0;
    const expiryDateHint = isExpiryDateAutoFilled && hasDepartmentReviewInterval
        ? t('departmentPolicies.editor.expiryDateAutoHint', { months: reviewMonths })
        : undefined;

    // Auto-populate expiry date from effective date + policy review interval if set in the variables
    useEffect(() => {
        if (!effectiveDate) return;
        if (!reviewMonths || isNaN(reviewMonths)) return;

        const currentExpiry = metaForm.getValues('expiryDate');
        if (currentExpiry && initialPolicyId && !isExpiryDateAutoFilled) return;
        if (currentExpiry && !isExpiryDateAutoFilled) return;

        const nextExpiryDate = addMonths(effectiveDate, reviewMonths);
        lastAutoFilledExpiryRef.current = nextExpiryDate;
        setIsExpiryDateAutoFilled(true);
        metaForm.setValue('expiryDate', nextExpiryDate);
    }, [effectiveDate, reviewMonths, initialPolicyId, isExpiryDateAutoFilled, metaForm]);

    useEffect(() => {
        if (!isExpiryDateAutoFilled) return;
        if (expiryDate === lastAutoFilledExpiryRef.current) return;

        lastAutoFilledExpiryRef.current = null;
        setIsExpiryDateAutoFilled(false);
    }, [expiryDate, isExpiryDateAutoFilled]);

    const initialDocumentData = {
        ...(initialDocData?.data ?? {}),
        policyConditionals:
            initialDocData?.data?.policyConditionals ??
            createEmptyPolicyConditionalsValue(),
    };
    const useSplitLayout = showPreview && isDesktopEditor;
    const showMobilePreview = !isDesktopEditor && showPreview && mobileView === 'preview';
    const showEditorPane = isDesktopEditor || mobileView === 'form' || !showPreview;

    return (
        <div className="flex h-full flex-col overflow-hidden">
            {/* Top bar */}
            <div className="sticky top-0 z-30 shrink-0 border-b border-subtle/60 bg-background/85 backdrop-blur-xl">
                <div className="flex flex-wrap items-center gap-3 px-4 py-4 sm:px-6">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/department-policies')}
                        className="shrink-0 text-muted-foreground hover:text-foreground"
                    >
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        {t('departmentPolicies.editor.policies')}
                    </Button>

                    <div className="min-w-0 flex-1">
                        <div className="mt-1 flex items-center gap-2">
                            <span className="truncate text-sm font-semibold text-foreground">
                                {title || t('departmentPolicies.editor.untitledPolicy')}
                            </span>
                        </div>
                    </div>

                    {!isDesktopEditor && showPreview && (
                        <div className="order-3 w-full">
                            <Tabs
                                value={mobileView}
                                onValueChange={(value) =>
                                    setMobileView(value as 'form' | 'preview')
                                }
                                className="w-full"
                            >
                                <TabsList className="grid w-full grid-cols-2 rounded-lg border border-subtle bg-background/60 p-1">
                                    <TabsTrigger value="form">{t('departmentPolicies.editor.form')}</TabsTrigger>
                                    <TabsTrigger value="preview">{t('departmentPolicies.editor.preview')}</TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </div>
                    )}

                    {isDesktopEditor && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowPreview((current) => !current)}
                            className="shrink-0"
                        >
                            {showPreview ? (
                                <EyeOff className="h-4 w-4 mr-1.5" />
                            ) : (
                                <Eye className="h-4 w-4 mr-1.5" />
                            )}
                            {showPreview ? t('departmentPolicies.editor.hidePreview') : t('departmentPolicies.editor.showPreview')}
                        </Button>
                    )}
                </div>
            </div>

            {/* Editor + preview */}
            <PolicyProvider
                initialData={initialDocumentData}
                policyId={initialPolicyId}
                documentData={initialDocData}
                getMeta={() => metaForm.getValues()}
                schemaKey={schemaKeyParam}
                templateId={templateIdParam}
                onCreated={handleCreated}
                onCompleted={handleCompleted}
                onSavedAndClose={handleSavedAndClose}
            >
                <div
                    ref={splitContainerRef}
                    className={`flex min-w-0 flex-1 ${
                        useSplitLayout ? 'overflow-hidden' : 'flex-col overflow-y-auto'
                    }`}
                >
                    {/* Left: metadata + form */}
                    {showEditorPane && (
                        <div
                            className="min-w-0 overflow-y-auto px-4 py-4 md:px-6"
                            style={
                                useSplitLayout
                                    ? {
                                          flexBasis: `${splitPercent}%`,
                                          flexShrink: 0,
                                          scrollbarWidth: 'none',
                                      }
                                    : {
                                          flexBasis: '100%',
                                          flexShrink: 0,
                                          scrollbarWidth: 'none',
                                      }
                            }
                        >
                            <div
                                className={`space-y-5 ${
                                    useSplitLayout
                                        ? ''
                                        : 'mx-auto w-full max-w-4xl'
                                }`}
                            >
                                <Card className="overflow-hidden rounded-lg border-subtle bg-card/90">
                                    <CardHeader className="border-b border-subtle pb-5">
                                        <CardTitle className="text-lg font-semibold text-foreground">
                                            {t('departmentPolicies.editor.policyDetails')}
                                        </CardTitle>
                                        <CardDescription className="text-sm text-muted-foreground/80 font-light">
                                            {t('departmentPolicies.editor.policyDetailsDescription')}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="pt-6">
                                        <PolicyMetadataBar
                                            schema={schema}
                                            metaForm={metaForm}
                                            departmentUsers={departmentUsers}
                                            usersLoading={usersLoading}
                                            expiryDateHint={expiryDateHint}
                                        />
                                    </CardContent>
                                </Card>

                                <Card className="overflow-hidden rounded-lg border-subtle bg-card/90">
                                    <CardHeader className="border-b border-subtle pb-5">
                                        <CardTitle className="text-lg font-semibold text-foreground">
                                            {t('departmentPolicies.editor.policyContent')}
                                        </CardTitle>
                                        <CardDescription className="text-sm text-muted-foreground/80 font-light">
                                            {t('departmentPolicies.editor.policyContentDescription')}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="pt-6">
                                        <Tabs
                                            value={activeEditorTab}
                                            onValueChange={(value) =>
                                                setActiveEditorTab(
                                                    value as 'content' | 'conditionals'
                                                )
                                            }
                                            className="w-full"
                                        >
                                            <TabsList className="grid w-full grid-cols-2 rounded-lg border border-subtle bg-background/60 p-1">
                                                <TabsTrigger value="content">
                                                    {t('departmentPolicies.editor.content')}
                                                </TabsTrigger>
                                                <TabsTrigger value="conditionals">
                                                    {t('departmentPolicies.editor.conditionals')}
                                                </TabsTrigger>
                                            </TabsList>

                                            <TabsContent value="content" className="mt-3 -mx-6">
                                                <DocumentForm
                                                    schema={schema}
                                                    variableOptions={variableOptions}
                                                    functions={policyFunctions}
                                                    visibleSectionIds={['content', 'actions']}
                                                />
                                            </TabsContent>

                                            <TabsContent value="conditionals" className="mt-3 -mx-6">
                                                <DocumentForm
                                                    schema={schema}
                                                    variableOptions={variableOptions}
                                                    functions={policyFunctions}
                                                    visibleSectionIds={['conditionals', 'actions']}
                                                />
                                            </TabsContent>
                                        </Tabs>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    )}

                    {/* Draggable divider */}
                    {useSplitLayout && (
                        <div
                            onMouseDown={handleDragStart}
                            className="group flex w-2 shrink-0 cursor-col-resize items-stretch transition-colors"
                        >
                            <div className="w-full bg-border/30 transition-colors group-hover:bg-sky-400/60" />
                        </div>
                    )}

                    {/* Right: live preview */}
                    {(useSplitLayout || showMobilePreview) && (
                        <div
                            className={`min-w-0 overflow-y-auto px-4 py-5 md:px-8 ${
                                useSplitLayout
                                    ? 'border-l border-subtle'
                                    : 'mx-auto w-full max-w-4xl'
                            }`}
                            style={useSplitLayout ? { flex: 1 } : undefined}
                        >
                            <LivePreviewPanel
                                schema={schema}
                                title={title}
                                variableMap={variableMap}
                                status={documentStatus}
                                effectiveDate={metaForm.watch('effectiveDate')}
                                expiryDate={metaForm.watch('expiryDate')}
                                tags={metaForm.watch('tags')}
                                authors={(metaForm.watch('coAuthors') ?? []).map((c) => c.name || 'Unknown')}
                                attachments={metaForm.watch('attachments') ?? []}
                            />
                        </div>
                    )}
                </div>
            </PolicyProvider>
        </div>
    );
}
